/**
 * Unit conversion for recipe/material quantities.
 * Direct pairs only: kg ↔ g, L ↔ ml.
 * Used so recipe overview/detail show one unit per family (e.g. all mass in g, all volume in ml).
 */

const MASS_UNITS = ['kg', 'g'];
const VOLUME_UNITS = ['l', 'ml'];  // lowercase to match norm()

const TO_BASE = {
    kg: 1000,   // 1 kg = 1000 g
    g: 1,
    l: 1000,    // 1 L = 1000 ml
    ml: 1,
};

/** Unit pairs for conversion - maps unit to its alternative */
const UNIT_PAIRS = {
    kg: 'g',
    g: 'kg',
    l: 'ml',
    ml: 'l',
};

/** Standard display casing: kg, g, L, ml (case-insensitive input → same output everywhere). */
const STANDARD_DISPLAY = {
    kg: 'kg',
    g: 'g',
    l: 'L',
    ml: 'ml',
};

/** Normalize unit string for lookup (lowercase, trim). */
function norm(u) {
    return (u || '').trim().toLowerCase();
}

/** Whether the unit is in a convertible pair (kg/g or L/ml). */
export function isConvertible(unit) {
    const u = norm(unit);
    return MASS_UNITS.includes(u) || VOLUME_UNITS.includes(u);
}

/** Return standard display string for unit (kg, g, L, ml). Case-insensitive input. */
export function formatUnitForDisplay(unit) {
    const u = norm(unit);
    if (STANDARD_DISPLAY[u] != null) return STANDARD_DISPLAY[u];
    return (unit || 'unit').trim() || 'unit';
}

/** Get the other unit in the pair: kg→g, g→kg, L→ml, ml→L. Returns null if not convertible. */
export function getPairedUnit(unit) {
    const u = norm(unit);
    const paired = UNIT_PAIRS[u];
    if (!paired) return null;
    // Return proper case: kg→g, g→kg, l→ml, ml→L
    if (u === 'l') return 'ml';
    if (u === 'ml') return 'L';
    return paired;
}

/** Get both units in a pair as an array [smaller, larger] or null if not convertible */
export function getUnitPair(unit) {
    const u = norm(unit);
    if (MASS_UNITS.includes(u)) {
        return ['g', 'kg'];
    }
    if (VOLUME_UNITS.includes(u)) {
        return ['ml', 'L'];
    }
    return null;
}

/** Check if two units are in the same conversion pair */
export function areUnitsCompatible(unit1, unit2) {
    const u1 = norm(unit1);
    const u2 = norm(unit2);
    if (u1 === u2) return true;
    const pair1 = getUnitPair(u1);
    const pair2 = getUnitPair(u2);
    if (!pair1 || !pair2) return false;
    return pair1[0] === pair2[0] && pair1[1] === pair2[1];
}

/**
 * Convert quantity from one unit to another (same family only).
 * @returns converted number or original qty if units are same or not convertible.
 */
export function convert(qty, fromUnit, toUnit) {
    const q = parseFloat(qty);
    if (!Number.isFinite(q)) return qty;
    const from = norm(fromUnit);
    const to = norm(toUnit);
    if (from === to) return q;
    const fromBase = TO_BASE[from];
    const toBase = TO_BASE[to];
    if (fromBase == null || toBase == null) return q;
    // Must be same family (both mass or both volume)
    const fromMass = MASS_UNITS.includes(from);
    const toMass = MASS_UNITS.includes(to);
    if (fromMass !== toMass) return q;
    const baseValue = q * fromBase;
    return baseValue / toBase;
}

/**
 * Convert to base unit (g for mass, ml for volume).
 * @returns { value: number, baseUnit: 'g'|'ml'|null } — null baseUnit means unit is not convertible.
 */
export function toBaseUnit(qty, unit) {
    const u = norm(unit);
    const q = parseFloat(qty) || 0;
    if (MASS_UNITS.includes(u)) return { value: q * (TO_BASE[u] || 1), baseUnit: 'g' };
    if (VOLUME_UNITS.includes(u)) return { value: q * (TO_BASE[u] || 1), baseUnit: 'ml' };
    return { value: q, baseUnit: null };
}

/**
 * Convert from base unit to target unit.
 */
export function fromBaseUnit(baseValue, baseUnit, targetUnit) {
    const to = norm(targetUnit);
    const toBase = TO_BASE[to];
    if (toBase == null) return baseValue;
    if (baseUnit === 'g' && MASS_UNITS.includes(to)) return baseValue / toBase;
    if (baseUnit === 'ml' && VOLUME_UNITS.includes(to)) return baseValue / toBase;
    return baseValue;
}

/**
 * Normalize (quantity, unit) for display so recipe uses one unit per family.
 * Default: mass → g, volume → ml (so no mixed kg+g or L+ml in same view).
 * @param options { massDisplay: 'g'|'kg', volumeDisplay: 'ml'|'L' }
 */
export function normalizeForDisplay(qty, unit, options = {}) {
    const massDisplay = (options.massDisplay || 'g').toLowerCase();
    const volumeDisplay = (options.volumeDisplay || 'ml').toLowerCase();
    const { value: baseValue, baseUnit } = toBaseUnit(qty, unit);
    if (baseUnit === 'g') {
        const displayUnit = massDisplay === 'kg' ? 'kg' : 'g';
        const displayQty = displayUnit === 'kg' ? baseValue / 1000 : baseValue;
        return { quantity: round(displayQty, 4), unit: displayUnit };
    }
    if (baseUnit === 'ml') {
        const displayUnit = volumeDisplay === 'l' ? 'L' : 'ml';
        const displayQty = displayUnit === 'L' ? baseValue / 1000 : baseValue;
        return { quantity: round(displayQty, 4), unit: displayUnit };
    }
    return { quantity: round(parseFloat(qty) || 0, 4), unit: formatUnitForDisplay(unit) };
}

/** Quantity in grams (for nutrition per 100g). kg→g, g→g. L/ml treated as water: 1 ml ≈ 1 g. */
export function quantityToGrams(qty, unit) {
    const u = norm(unit);
    const q = parseFloat(qty) || 0;
    if (u === 'kg') return q * 1000;
    if (u === 'g') return q;
    if (u === 'l') return q * 1000;
    if (u === 'ml') return q;
    return q;
}

/** Quantity in ml (for volume). L→ml, ml→ml. */
export function quantityToMl(qty, unit) {
    const u = norm(unit);
    const q = parseFloat(qty) || 0;
    if (u === 'l') return q * 1000;
    if (u === 'ml') return q;
    return q;
}

function round(n, decimals) {
    if (!Number.isFinite(n)) return 0;
    const d = Math.pow(10, decimals);
    return Math.round(n * d) / d;
}
